import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { RichSelectModule } from '@ag-grid-enterprise/rich-select';
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection';
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export';
import { CsvExportModule } from '@ag-grid-community/csv-export';
import { ModuleRegistry } from '@ag-grid-community/core';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { IController, ITimeoutService } from 'angular';
import * as _ from 'lodash';
import { ImUser } from 'Src/ng2/shared/services/im-models/im-user';
import { SorterColumnDataType } from '../../../ng2/shared/constants/sorter-column-data-type.constant';
import { IUserMini } from '../../../ng2/shared/typings/interfaces/user.interface';
import { SdcActionsService } from '../flux/sdc-actions.service';
import { ISdcStore } from '../flux/sdc-store.interface';
import { TValidSdcColumns } from '../sdc-columns.type';
import { SpinnerModal } from './../../shared/modals/status-spinner/status-spinner.service';
import { IEvents, PusherDOMHelper } from './../../shared/services/pusher-dom-helper/pusher-dom-helper.service';
import { StudentSetService } from './../../shared/services/sorter/student-set.service';
import { ISorterColumn } from './../../shared/sorter-column/sorter-column.service';
import { SdcStateService } from './../sdc-state/sdc-state.service';
import { IGridState, ISdcActivity, TValidSdcKeys, TValidSdcViews } from './../sdc-state/sdc-state.typings';

declare const window;

type TValidButtonStates = 'CLEAR' | 'SELECT';

export class NewSdcGridCtrl implements IController {
  static $inject: string[] = [
    '$scope',
    '$timeout',
    'StudentSet',
    'SDCStore',
    'SorterColumn',
    'sdcActions',
    'ErrorModal',
    'Blob',
    'FileSaver',
    'ImStudent',
    'ImStudentRegents',
    '$mdToast',
    '$state',
    '$location',
    '$mdDialog',
    'NavService',
    '$rootScope',
    'ErrorToaster',
    'ObjectCache',
    'Rollbar',
    '$analytics',
    'SpinnerModal',
    'SdcStateService',
    'User',
    'PusherDOMHelper',
    'ImUser',
  ];
  // class properties
  public channel;
  public currentSchool;
  public currentUser;
  public sdcView: TValidSdcViews = 'grid';
  public sdcKey: TValidSdcKeys;

  private _agGridApi: any = null;
  private shouldBeListening: any = {};
  private enumAgGridValueToSdcValue = {
    '-': null,
    '(Empty)': '',
  };
  // Aligns blank and empty array values between grid and sdc store
  private arrayAgGridValueToSdcValue = {
    '(Blanks)': '[]',
  };
  private enumSdcValueToAgGridValue = new Map();
  private arraySdcValueToAgGridValue = new Map();
  private filterCount;
  private initialGridState: IGridState;
  private currentActivity;
  private numTotalStudents = 0;
  private numDisplayedStudents = 0;
  private fullSorterColumnSet = [];
  private colDiff;

  // https://www.ag-grid.com/javascript-grid-column-menu/#gsc.tab=0
  private getMainMenuItems(params) {
    return _.remove(params.defaultItems, menuItem => {
      return menuItem !== 'toolPanel';
    });
  }

  public gridOptions = {
    columnDefs: [],
    defaultColDef: { filterable: true, resizable: true },
    rowData: [],
    rowSelection: 'multiple',
    suppressRowClickSelection: true,
    rowDeselection: true,
    overlayLoadingTemplate: '<div></div>',
    headerHeight: 72,
    enableRangeSelection: true,
    // angularCompileHeaders: true,
    suppressColumnMoveAnimation: true,
    columnApi: null,
    getMainMenuItems: this.getMainMenuItems,
    getContextMenuItems: this.getContextMenuItems,
  };

  constructor(
    private $scope,
    private $timeout: ITimeoutService,
    private StudentSet: StudentSetService,
    private SDCStore: ISdcStore,
    private SorterColumn: any,
    private sdcActions: SdcActionsService,
    private ErrorModal: any,
    private Blob,
    private FileSaver,
    private ImStudent,
    private ImStudentRegents,
    private $mdToast,
    private $state,
    private $location,
    private $mdDialog,
    private NavService,
    private $rootScope,
    private ErrorToaster,
    private ObjectCache,
    private Rollbar,
    private $analytics,
    private SpinnerModal: SpinnerModal,
    private SdcStateService: SdcStateService,
    private User,
    private pusherDOMHelper: PusherDOMHelper,
    private imUser: ImUser,
  ) {}

  $onInit() {
    ModuleRegistry.registerModules([
      ClientSideRowModelModule,
      ClipboardModule,
      CsvExportModule,
      ExcelExportModule,
      MenuModule,
      RangeSelectionModule,
      RichSelectModule,
      SetFilterModule,
    ]);

    this.$scope.$listenTo(this.SDCStore, ['ui', 'isLoading'], () => {
      const isLoading = this.SDCStore.getUiState().isLoading;
      if (!isLoading) this.setUi();
    });
  }

  set agGridApi(agGridApi) {
    this._agGridApi = agGridApi;
  }

  get agGridApi() {
    return this._agGridApi;
  }

  setUi() {
    // set agGridApi: we want to wait until the store is ready before firing `onGridIsReady`
    this.$scope.$watch('$ctrl.gridOptions.api', agGridApi => {
      if (agGridApi) this.onGridIsReady(agGridApi);
    });

    // set permissioning for sorter columns
    this.SorterColumn.setEditablePermissions(this.currentUser);

    this.NavService.closeNav('left');
    this.sdcKey = this.SDCStore.getSdcKeyFromUiState();
    this.currentActivity = this.getCurrentActivity();
    this.colDiff = _.difference(this.fullSorterColumnSet, this.currentActivity.columns);

    this.startPusherListeners();
  }

  startPusherListeners(): void {
    // courseDiff & gapPlans pusher event handler : written to handle bulk adding (not yet possible)
    const gapPlanAndCourseDiffAddedHandler = (studentIdKey: string, data: Object): void => {
      const count = Object.keys(data[studentIdKey]).length;
      this.showToast.call(this, count);
    };

    const gapPlanAndCourseDiffUpdateHandler = (data: Object): void => {
      // can only patch one courseDiff of gapPlan at a tiem
      this.showToast.call(this, 1);
    };

    // display for all SDCs
    const sdcsToListenOn = null;
    const subscriptionDetails: IEvents = {
      'course-diff-added': {
        handler: gapPlanAndCourseDiffAddedHandler.bind(null, 'studentIdsToCourseDiffIds'),
        sdcKeys: sdcsToListenOn,
      },
      'course-diff-patched': {
        handler: gapPlanAndCourseDiffUpdateHandler,
        sdcKeys: sdcsToListenOn,
      },
      'gap-plan-added': {
        handler: gapPlanAndCourseDiffAddedHandler.bind(null, 'studentIdsToGapPlanIds'),
        sdcKeys: sdcsToListenOn,
      },
      'gap-plan-patched': {
        handler: gapPlanAndCourseDiffUpdateHandler,
        sdcKeys: sdcsToListenOn,
      },
    };
    // binding events : which returns an object of unsubscribe methods
    const unsubscribeObj = this.pusherDOMHelper.bindEventsToChannel(this.channel, subscriptionDetails);
    // unbinding events on $destroy
    this.$scope.$on('$destroy', () => this.pusherDOMHelper.unbindEventsFromChannel(this.channel, unsubscribeObj));
  }

  // options to show on context menu (right click) in ag grid cell.
  // options can be found here: https://www.ag-grid.com/javascript-grid-context-menu/
  private getContextMenuItems(params) {
    const result = ['copy', 'copyWithHeaders', 'paste', 'separator', 'export'];
    return result;
  }

  private onGridIsReady(agGridApi) {
    this.agGridApi = agGridApi;

    // These listeners sync SDCStore -> grid
    this.$scope.$listenTo(this.SDCStore, ['flattenedStudents'], this.applyFlattenedStudentsAndFilter.bind(this));
    this.$scope.$listenTo(this.SDCStore, ['columnKeys'], this.applyColumnKeys.bind(this));
    this.$scope.$listenTo(this.SDCStore, ['hiddenColumnKeys'], this.applyHiddenColumnKeys.bind(this));
    this.$scope.$listenTo(this.SDCStore, ['filter'], this.applyFilter.bind(this));
    this.$scope.$listenTo(this.SDCStore, ['sort'], this.applySort.bind(this));
    this.$scope.$listenTo(this.SDCStore, ['lockedFilters'], this.applyLockedFilters.bind(this));
    this.$scope.$listenTo(this.SDCStore, ['selectedStudentIds'], this.applySelection.bind(this));
    this.$scope.$listenTo(this.SDCStore, ['ui', 'isLoading'], this.applyUiIsLoading.bind(this));

    // These event listeners sync grid -> SDCStore
    agGridApi.addEventListener('beforeFilterChanged', this.onBeforeFilterChanged.bind(this));
    agGridApi.addEventListener('filterChanged', this.onAfterFilterChanged.bind(this));
    agGridApi.addEventListener('selectionChanged', this.onSelectionChanged.bind(this));
    agGridApi.addEventListener('sortChanged', this.onSortChanged.bind(this));

    // When you call most programmatic methods to update AgGrid, AgGrid triggers the event handlers. This is not
    // desirable because the event handlers themselves trigger sdcActions. This causes thrashing. What we do to get
    // around this is temporarily "turn off" the event handlers while we are doing a large update to the grid.
    this.shouldBeListening = {
      beforeFilterChanged: true,
      afterFilterChanged: true,
      selectionChanged: true,
      sortChanged: true,
    };

    this.enumSdcValueToAgGridValue.set(null, '-');
    this.enumSdcValueToAgGridValue.set('', '(Empty)');
    this.arraySdcValueToAgGridValue.set('[]', '(Blanks)');

    _.each(this.SorterColumn, (v, k) => {
      this.fullSorterColumnSet.push(k);
    });

    // If a user changes the filter in the grid, the agGrid filter listener will get fired, calling
    // onAfterFilterChange.  This will updat the SDCStore.filter, which will cause this listener to get
    // fired, which will call syncStoreFilterToQueryParam to update the URL query param
    this.$scope.$listenTo(this.SDCStore, ['filter'], () => {
      let newFilter = this.SDCStore.getFilter();
      this._updateStudentCounts();
      this.filterCount = _.keys(newFilter).length;
      this.syncStoreFilterToQueryParam();
    });

    // listen to sort
    this.$scope.$listenTo(this.SDCStore, ['sort'], () => {
      this.syncStoreSortToQueryParam();
    });
  }

  private showToast(count) {
    const toastScope = this.$rootScope.$new();

    toastScope.message =
      count === 1 ? `${count} student record was changed.` : `${count} student records were changed.`;

    this.$mdToast
      .show({
        hideDelay: 1000,
        position: 'bottom left',
        template: require('../../student/stateless-components/stale-student-toast/stale-student-toast.html'),
        scope: toastScope,
      })
      .catch(err => {
        if (err) this.Rollbar.warning(err);
        this.Rollbar.warning('Failed to show toast');
      });
  }

  private _updateStudentCounts() {
    this.numTotalStudents = this.SDCStore.getAllStudents().length;
    this.numDisplayedStudents = this.SDCStore.getFilteredStudents().length;
  }

  private getCurrentActivity(): ISdcActivity {
    const uiState = this.SDCStore.getUiState();
    const state = uiState.currentActivity.state;
    return this.SdcStateService.determineSdcActivityFromState(state);
  }

  // Updates the ?filter= query param hash with the hash of the filter from the store
  private syncStoreFilterToQueryParam(_filter?) {
    const filter = _filter || this.SDCStore.getFilter();
    const filterHash = this.ObjectCache.cacheObject(filter);
    this.$location.search('filter', filterHash).replace();
    const gridView = this.initialGridState.gridView;
    if (gridView) this.$location.search('gridView', gridView).replace();
  }

  // Updates the ?sort= query param hash with the filter from the store
  private syncStoreSortToQueryParam(_sort?) {
    const sort = _sort || this.SDCStore.getSort();
    const sortHash = this.ObjectCache.cacheObject(sort);
    this.$location.search('sort', sortHash).replace();
  }

  private getUserAddedColumns() {
    const sdcState = this.SDCStore.getCurrentSdcState(this.sdcKey);
    const { userAddedColumns } = sdcState.grid;
    return userAddedColumns;
  }

  syncStoreUserAddedColumnToQueryParam(_userAddedColumns?) {
    const userAddedColumns = _userAddedColumns || this.getUserAddedColumns() || [];
    const userAddedColumnHash = this.ObjectCache.cacheObject(userAddedColumns);
    this.$location.search('userAddedColumns', userAddedColumnHash).replace();
  }

  // LISTENERS
  // Functions that apply state to the grid ----

  private handleFetchError(err) {
    // Errors with status of -1 are due to cancelled requests from changing routes before the request can resolve
    if (err.status !== -1) {
      this.ErrorToaster.showError(err);
    }
  }

  public refreshColumns(columnsToAdd) {
    this.$mdDialog.hide('hiding').then(() => {
      // Load any user added columns to currentSdcState
      this.sdcActions.appendUserAddedColumns(this.sdcKey, this.sdcView, columnsToAdd);

      // Fetch data associated with any new columns via SDC store/actions
      this.sdcActions
        .fetchAdditionalColumnsAsync(columnsToAdd)
        .then(() => {
          // Log each column added as it's own event??
          _.forEach(columnsToAdd, newColumn => {
            this.$analytics.eventTrack('Column Added', { label: newColumn });
          });

          // Sync new columns with Query params
          this.syncStoreUserAddedColumnToQueryParam();
        })
        .catch(this.handleFetchError);
    });
  }

  public applyFilter() {
    const newFilter = this.SDCStore.getFilter();
    const agGridFilterModel = this.agGridApi.getFilterModel();
    const agGridGridColumns = this.agGridApi.columnController.getAllGridColumns();

    const existingFilter = this.mapFilterToSdcStoreFormat(agGridFilterModel);
    const existingColumns = _.map(agGridGridColumns, (col: { colId: TValidSdcColumns }) => {
      return col.colId;
    });

    const filterIsSame = angular.equals(newFilter, existingFilter);
    if (!filterIsSame) {
      const pickedNewFilter = _.pickBy(newFilter, (value, key) => {
        return _.includes(existingColumns, key);
      });
      this.shouldBeListening.beforeFilterChanged = false;
      this.shouldBeListening.afterFilterChanged = false;
      const mappedNewFilter = this.mapFilterToAgGridFormat(angular.copy(pickedNewFilter)); // copy it to make it mutable

      _.each(existingFilter, (value, key) => {
        this.agGridApi.destroyFilter(key);
        this.agGridApi.setFilterModel(null);
      });

      this.agGridApi.setFilterModel(mappedNewFilter);
      this.shouldBeListening.beforeFilterChanged = true;
      this.shouldBeListening.afterFilterChanged = true;
    }

    this.monkeyPatchFilterManager();
  }

  // This monkey-patches the AgGrid FilterManager to make any students with IDs in SDCStore.recentEditedStudentIds
  // show up, no matter the filter. AgGrid internally calls FilterManager#doesRowPassFilter for every row whenever
  // it redraws the grid. We monkey patch that method so that it always returns true for rows in
  // recentEditedStudentIds. Note that we need to try to monkey patch each time the filter changes because there is
  // no FilterManager until the first filter has been added.
  private monkeyPatchFilterManager() {
    if (!this.agGridApi.filterManager._nv_hasDoesRowPassFilterBeenMonkeyPatched) {
      const _doesRowPassFilter = this.agGridApi.filterManager.doesRowPassFilter;
      this.agGridApi.filterManager.doesRowPassFilter = function nvMonkey_doesRowPassFilter(node, filterToSkip) {
        const recentEditIds = this.SDCStore.getRecentEditedStudentIds();
        if (_.includes(recentEditIds, node.data._id)) return true;
        return _doesRowPassFilter.call(this.agGridApi.filterManager, node, filterToSkip);
      }.bind(this);

      // don't need to monkey patch again
      this.agGridApi.filterManager._nv_hasDoesRowPassFilterBeenMonkeyPatched = true;
    }
  }

  public applySort() {
    const newSort = this.SDCStore.getSort();
    const existingSort = this.agGridApi.getSortModel();
    const sortsAreEqual = angular.equals(existingSort, newSort);
    if (!sortsAreEqual) {
      this.shouldBeListening.sortChanged = false;
      this.agGridApi.setSortModel(newSort);
      this.shouldBeListening.sortChanged = true;
    }
  }

  public applyColumnKeys() {
    const sdcColumnKeys = this.SDCStore.getColumnKeys();
    const allColumnKeys = _.union(['gridRowSelector'], sdcColumnKeys);
    const userMinis = this.SDCStore.getAllSchoolUsers();
    const colDefs = this.StudentSet.getColumnDefs(allColumnKeys, userMinis);
    const hiddenColumnKeys = new Set(this.SDCStore.getHiddenColumnKeys());

    // We need to add newValueHandlers to the columns to intercept cell edits before AgGrid tries to save data to the
    // flattenedStudents. This is because the flattenedStudents are immutable, so AgGrid would throw an error if it
    // tries to do that.
    _.each(colDefs, colDef => {
      colDef.newValueHandler = this.onCellNewValue.bind(this);
      colDef.hide = hiddenColumnKeys.has(colDef.field);
    });

    this.agGridApi.setColumnDefs(colDefs);
    this.syncStoreUserAddedColumnToQueryParam();
  }

  public applyHiddenColumnKeys() {
    // Make sure this function is called after the columns are loaded!
    const columnKeys = this.SDCStore.getColumnKeys();
    const hiddenColumnKeys = this.SDCStore.getHiddenColumnKeys();
    const visibleColumnKeys = _.difference(columnKeys, hiddenColumnKeys);
    this.gridOptions.columnApi.setColumnsVisible(visibleColumnKeys, true);
    this.gridOptions.columnApi.setColumnsVisible(hiddenColumnKeys, false);
  }

  public applyFlattenedStudentsAndFilter() {
    const flattenedStudents = this.SDCStore.getAllFlattenedStudents();
    this.agGridApi.setRowData(flattenedStudents);
    // Need to apply the filter and selection in a timeout since AgGrid needs to process the rows first
    this.$timeout(() => {
      this.applyFilter();
      this.applySelection();
    });
  }

  public applySelection() {
    const selectedStudentIds = this.SDCStore.getSelectedStudentIds();

    // Stop listening for selectionChanged events for this loop, otherwise this will cause a ton of thrashing.
    this.shouldBeListening.selectionChanged = false;
    this.agGridApi.forEachNode(node => {
      const shouldBeSelected = _.includes(selectedStudentIds, node.data._id);
      if (node.isSelected() !== shouldBeSelected) {
        node.setSelected(shouldBeSelected);
      }
    });
    this.shouldBeListening.selectionChanged = true;
  }

  public applyLockedFilters() {
    const filterManager = this.agGridApi.filterManager;

    // if there is no filter manager, then the filters haven't been added to the grid yet
    if (!filterManager) {
      console.warn('Cannot lock filters because filters have not been added yet. Add filters first, then lock them.');
      return;
    }

    const allFilters: TValidSdcColumns[] = filterManager.allFilters;
    const lockedFilters = this.SDCStore.getLockedFilters();
    // check if this _.each needs a second param in iteratee SR
    _.each(allFilters, (filter, col: TValidSdcColumns) => {
      if (_.includes(lockedFilters, col)) {
        this.lockFilter(filter);
      } else {
        this.unlockFilter(filter);
      }
    });
  }

  public lockFilter(agFilter) {
    if (agFilter._nv_disabledGuiElement) return; // already locked
    agFilter._nv_disabledGuiElement = agFilter.gui.firstChild;
    agFilter.gui.removeChild(agFilter._nv_disabledGuiElement);
    const disabledMsgElement = document.createElement('div');
    disabledMsgElement.innerHTML = 'This filter is locked.';
    disabledMsgElement.setAttribute('style', 'padding: 5px;');
    agFilter.gui.appendChild(disabledMsgElement);
  }

  public unlockFilter(agFilter) {
    if (!agFilter._nv_disabledGuiElement) return; // already unlocked
    agFilter.gui.removeChild(agFilter.gui.firstChild); // remove locked message
    agFilter.gui.appendChild(agFilter._nv_disabledGuiElement);
    delete agFilter._nv_disabledGuiElement;
  }

  private applyUiIsLoading() {
    const isLoading = this.SDCStore.getUiState().isLoading;
    if (isLoading) {
      this.agGridApi.showLoadingOverlay();
    } else {
      this.agGridApi.hideOverlay();
      this.$timeout(() => {
        this.SpinnerModal.deactivateSpinner();
      }, 0);
    }
  }

  // Event handlers that handle user-initiated grid state changes ----
  private onBeforeFilterChanged() {
    if (!this.shouldBeListening.beforeFilterChanged) return;
    // Whenever the filter changes, the SDCStore will reset the recentEditedStudentIds array. We preemptively do it
    // here because right after this event fires, the doesRowPassFilter monkey patch is going to run, and we need
    // SDCStore#getRecentEditedStudentIds() to return an empty array.
    this.sdcActions.setRecentEditedStudentIds([]);
  }

  private onAfterFilterChanged() {
    if (!this.shouldBeListening.afterFilterChanged) return;
    const agGridFilter = this.agGridApi.getFilterModel();
    const newFilter = this.mapFilterToSdcStoreFormat(agGridFilter);
    const sdcView = this.sdcView;
    const sdcKey = this.sdcKey;
    this.sdcActions.setEntireFilter({ newFilter, sdcView, sdcKey });
  }

  private onSelectionChanged() {
    if (!this.shouldBeListening.selectionChanged) return;
    const selectedNodes = this.agGridApi.getSelectedNodes();
    const sortedSelectedNodes = _.sortBy(selectedNodes, ({ childIndex }) => childIndex);
    const selectedIds = _.map(sortedSelectedNodes, ({ data: { _id } }) => _id);
    this.sdcActions.setSelectedStudentIds(selectedIds);
  }

  private onSortChanged() {
    if (!this.shouldBeListening.sortChanged) return;
    const newSort = this.agGridApi.getSortModel();
    const sdcView = this.sdcView;
    const sdcKey = this.sdcKey;
    this.sdcActions.setSort({ newSort, sdcKey, sdcView });
  }

  private onCellNewValue(params) {
    const { data, colDef, newValue, oldValue } = params;
    const { field } = colDef;
    const col: ISorterColumn = this.SorterColumn.getByColumnKey(field);
    const parsedNewValue = this.parseNullableCol(col, newValue);

    if (parsedNewValue !== oldValue) {
      const { _id } = data;
      const { path } = col;

      this.sdcActions.updateStudentFieldByPathAsync(_id, path, parsedNewValue, { immediate: true }).catch(error => {
        this.ErrorModal.show('Error updating student', error);
      });
    }
  }

  // The cell editor for BOOLEAN_YES_NO and ENUM fields will send back "-" as the newValue when the user is trying
  // to set the column to "null". This handles that special case. Unfortunately it isn't possible to get the
  // build-in AgGrid agRichSelectCellEditor cell editor to support nulls directly.
  // For STRING cols, AG Grid changes a `null` value to `''` when clicking on a cell and not making a change; hence,
  // we need to turn the `''` back to a `null`
  private parseNullableCol(col: ISorterColumn, newValue) {
    const isColNullable = col => {
      const { dataType, dataTypeOptions } = col;
      const { BOOLEAN_YES_NO } = SorterColumnDataType;

      if (dataType === BOOLEAN_YES_NO || (dataTypeOptions && dataTypeOptions.canBeNull)) return true;
      return false;
    };

    if (isColNullable(col)) {
      // if '', coerce to `null` / issue: https://github.com/ag-grid/ag-grid/issues/479
      if (newValue === '-' || newValue === '') newValue = null;
    }

    return newValue;
  }

  // This handles converting "-" back to `null` and "(Empty)" back to an empty string before assigning the filter to
  // the SDCStore. See StudentSet.getColumnDefs for more details.
  private mapFilterToSdcStoreFormat(agGridFilter) {
    return _.reduce(
      agGridFilter,
      (storeFilter, filterVal: any, key: string) => {
        const sorterCol = this.SorterColumn.getByColumnKey(key);

        // For debugging, to help find when keys are undefined
        if (!sorterCol) {
          const warning = new Error(`key "${key}" does not exist on SorterColumn constant.
        Here is the filter: ${JSON.stringify(agGridFilter)}`);
          console.warn(warning);
          this.Rollbar.error(warning);
          return storeFilter;
        }

        const dataType = sorterCol.dataType;

        if (dataType === SorterColumnDataType.ENUM) {
          // previously filterVal.map
          storeFilter[key] = filterVal.values.map(v => {
            return this.enumAgGridValueToSdcValue.hasOwnProperty(v) ? this.enumAgGridValueToSdcValue[v] : v;
          });
        } else if (dataType === SorterColumnDataType.ARRAY) {
          storeFilter[key] = filterVal.values.map(v => {
            const newVal = this.arrayAgGridValueToSdcValue.hasOwnProperty(v) ? this.arrayAgGridValueToSdcValue[v] : v;
            return newVal;
          });
        } else if (dataType === SorterColumnDataType.USER_MINI) {
          const users: IUserMini[] = this.convertFilterValToUserMinis(filterVal);
          storeFilter[key] = users;
        } else {
          storeFilter[key] = filterVal.values ? filterVal.values : filterVal;
        }

        return storeFilter;
      },
      {},
    );
  }

  private convertFilterValToUserMinis(filterVals): IUserMini[] {
    const allSchoolUsers = this.SDCStore.getAllSchoolUsers();
    const filteredSchoolUsers = filterVals.values.map(lastName => {
      const user = allSchoolUsers.find(u => u.lastName === lastName);
      return user || this.imUser.generateNullUser();
    });
    return filteredSchoolUsers;
  }

  private mapFilterToAgGridFormat(storeFilter) {
    const agGridFormat = _.reduce(
      storeFilter,
      (agGridFilter, filterVal: any, key) => {
        const dataType = this.SorterColumn.getByColumnKey(key).dataType;

        if (dataType === SorterColumnDataType.ENUM) {
          agGridFilter[key] = filterVal.map(filterSubVal => {
            const mappedValue = this.enumSdcValueToAgGridValue.get(filterSubVal);
            const newVal = !_.isUndefined(mappedValue) ? mappedValue : filterSubVal;
            return newVal;
          });
        } else if (dataType === SorterColumnDataType.ARRAY) {
          agGridFilter[key] = filterVal.map(filterSubVal => {
            const mappedValue = this.arraySdcValueToAgGridValue.get(filterSubVal);
            const newVal = !_.isUndefined(mappedValue) ? mappedValue : filterSubVal;
            return newVal;
          });
        } else if (dataType === SorterColumnDataType.NUMERIC && filterVal.type === 7) {
          agGridFilter[key] = {
            filter: filterVal.filter,
            type: filterVal.type,
          };
        } else if (dataType === SorterColumnDataType.USER_MINI) {
          // The AG Grid filter can currently only accommodate a single string, so using last name here
          const users = filterVal.values.map((userMini: IUserMini) => {
            return userMini.lastName || '(None)';
          });
          agGridFilter[key] = users;
        } else {
          agGridFilter[key] = filterVal;
        }

        return agGridFilter;
      },
      {},
    );

    return agGridFormat;
  }

  public exportDataAsCsv() {
    const type = 'text/csv;charset=utf-8';
    const keys = this.SDCStore.getColumnKeys();
    const hiddenKeys = this.SDCStore.getHiddenColumnKeys();

    // filter hiddenKeys to match csv to grid view
    const columnKeys = keys.filter(key => hiddenKeys.indexOf(key) === -1);

    // agGrid csv export option for custom value processing
    // used here to destructure data when exporting a mini-schema to csv
    const processCellCallback = param => {
      const { value } = param;
      const isUserMini = value && typeof value === 'object' && value.hasOwnProperty('userId');
      if (isUserMini) {
        if (!value.userId) {
          return '(None)';
        } else if (this.imUser.getEffectiveEmail(value)) {
          // split used to remove prepended ' ' in usermini firstName data
          return `${value.lastName}, ${value.firstName.split(' ').join('')} (${this.imUser.getEffectiveEmail(value)})`;
        } else {
          return this.imUser.getFullNameWithEmailFromMini(value);
        }
      } else {
        return value;
      }
    };

    let csvString = this.agGridApi.getDataAsCsv({ columnKeys, processCellCallback });
    this._saveCsvFile(csvString, 'Grid Export.csv', type);
    this.$analytics.eventTrack('Download', { category: 'Grid Exports', label: 'Raw CSV Export' });
  }

  public _saveCsvFile = (csvString, fileName, type) => {
    const blob = new this.Blob([csvString], { type });
    this.FileSaver.saveAs(blob, fileName);
  };
}
